import { useEffect, useState } from 'react'
import { AlertCircle, BookOpen, ListTree, Tag, Layers } from 'lucide-react'

import { DashboardLayout } from '@/components/layouts/DashboardLayout'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/lib/auth-context'
import { PERMISSIONS } from '@/lib/permissions'
import { cn } from '@/lib/utils'
import { showErrorToast } from '@/lib/toast'

import { TranslationsTable } from '@/components/features/catalog-translations/TranslationsTable'
import { AttributeLabelsTable } from '@/components/features/catalog-translations/AttributeLabelsTable'
import { OptionLabelsTable } from '@/components/features/catalog-translations/OptionLabelsTable'
import { CultureBadge } from '@/components/features/catalog-translations/CultureBadge'

import { getCatalogTranslations } from '@/lib/api/v2/catalog-translations'
import type { CatalogTranslationsPayload } from '@/types/catalog-translations'

type Section = 'translations' | 'attributes' | 'options'

const SECTIONS: { id: Section; label: string; icon: React.ElementType }[] = [
  { id: 'translations', label: 'Traductions produits', icon: BookOpen },
  { id: 'attributes', label: 'Labels d\'attributs', icon: Tag },
  { id: 'options', label: 'Option sets', icon: ListTree },
]

export default function CatalogTranslationsPage() {
  const { user } = useAuth()
  const [section, setSection] = useState<Section>('translations')
  const [payload, setPayload] = useState<CatalogTranslationsPayload | null>(null)
  const [loading, setLoading] = useState(true)

  const canManage = user?.permissions?.includes(PERMISSIONS.SETTINGS_MANAGE) === true

  useEffect(() => {
    if (!canManage) return
    let cancelled = false
    const load = async () => {
      try {
        const p = await getCatalogTranslations()
        if (!cancelled) setPayload(p)
      } catch {
        if (!cancelled) showErrorToast('Erreur lors du chargement du catalogue.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [canManage])

  if (!canManage) {
    return (
      <DashboardLayout>
        <div className="max-w-xl mx-auto py-10 px-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Accès refusé</AlertTitle>
            <AlertDescription>
              Vous n'avez pas les droits pour accéder à l'administration du catalogue.
            </AlertDescription>
          </Alert>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-xl font-semibold">Catalogue · Traductions</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Vue de consultation des produits, attributs et options du catalogue, avec leurs traductions par langue.
          </p>
        </div>

        <CatalogMeta payload={payload} loading={loading} />

        <div className="flex gap-6">
          {/* Sidebar nav */}
          <nav className="w-56 shrink-0 space-y-1">
            {SECTIONS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setSection(id)}
                data-testid={`catalog-translations-sidebar-${id}`}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors text-left',
                  section === id
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                )}
              >
                <Icon className="size-4 shrink-0" />
                {label}
              </button>
            ))}
          </nav>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {section === 'translations' && (
              <div className="space-y-4">
                <SectionHeader
                  title="Produits × cultures"
                  hint="Cliquez une cellule pour basculer entre rendu live (sample bag) et template brut."
                />
                {loading && <div className="text-sm text-muted-foreground">Chargement…</div>}
                {!loading && payload && (
                  <TranslationsTable
                    products={payload.products}
                    cultures={payload.supportedCultures}
                  />
                )}
              </div>
            )}

            {section === 'attributes' && (
              <div className="space-y-4">
                <SectionHeader
                  title="Labels d'attributs partagés"
                  hint="Liste des clés i18n référencées par les attributs des produits, avec leur résolution par culture."
                />
                <AttributeLabelsTable />
              </div>
            )}

            {section === 'options' && (
              <div className="space-y-4">
                <SectionHeader
                  title="Option sets DB"
                  hint="Listes de valeurs configurables (ex. matériaux de plan de travail) avec leurs items et traductions par culture."
                />
                <OptionLabelsTable />
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}

function SectionHeader({ title, hint }: { title: string; hint: string }) {
  return (
    <div>
      <h2 className="text-base font-semibold">{title}</h2>
      <p className="text-sm text-muted-foreground mt-0.5">{hint}</p>
    </div>
  )
}

function CatalogMeta({ payload, loading }: { payload: CatalogTranslationsPayload | null; loading: boolean }) {
  if (loading || !payload) return null
  const { supportedCultures, products, legacyInMemory } = payload

  return (
    <div className="rounded-md border bg-muted/30 px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        <div className="flex items-center gap-2">
          <Layers className="size-4 text-muted-foreground" />
          <span className="text-muted-foreground">Catalogue DB :</span>
          <span className="font-medium">{products.length} produits</span>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Cultures :</span>
          <div className="flex items-center gap-1">
            {supportedCultures.map(c => <CultureBadge key={c} culture={c} />)}
          </div>
        </div>

        {legacyInMemory.enabled && legacyInMemory.count > 0 && (
          <div className="flex items-center gap-2 ml-auto" data-testid="catalog-translations-legacy-meta">
            <Badge variant="outline" className="text-[11px] border-amber-500/50 text-amber-700 dark:text-amber-300">
              {legacyInMemory.count} famille{legacyInMemory.count > 1 ? 's' : ''} legacy in-memory
            </Badge>
            <span className="text-xs text-muted-foreground">
              (Scriban legacy, condamnées Phase 7)
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
